/**
 * 🔔 通知カウント管理のフック
 */
import { useState, useEffect, useCallback } from 'react';

// API URL設定を直接埋め込み（Vercel対応）
const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  
  const isLocalhost = 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();

export const useNotificationCount = () => {
  const [counts, setCounts] = useState({
    chat: 0,
    notifications: 0,
    stakes: 0,
    project: 0,
    store: 0,
    friends: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 通知カウントを取得
  const fetchNotificationCounts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // ユーザー情報を安全に取得
      let user = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        console.debug('ユーザー情報の解析に失敗:', e);
        user = {};
      }

      // ゲストユーザー、権限レベル2（アクター未作成）、またはトークンがない場合は通知機能をスキップ
      if (!token || user.isGuest || user.role === 'guest' || user.permission_level === 2) {
        // ゲストユーザー、権限レベル2（アクター未作成）、または認証なしの場合は通知カウントを0に設定
        setCounts({
          chat: 0,
          notifications: 0,
          stakes: 0,
          project: 0,
          store: 0,
          friends: 0,
        });
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/notifications/counts`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // レスポンスがJSONかチェック
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          if (data.success) {
            setCounts(data.counts);
          } else {
            console.error('通知カウント取得失敗:', data.message);
            setCounts({
              chat: 0,
              notifications: 0,
              stakes: 0,
              project: 0,
              store: 0,
              friends: 0,
            });
          }
        } else {
          // HTMLレスポンスの場合（APIエラー）
          const text = await response.text();
          console.error(
            'API応答がJSON形式ではありません:',
            text.substring(0, 200)
          );
          setCounts({
            chat: 0,
            notifications: 0,
            stakes: 0,
            project: 0,
            store: 0,
            friends: 0,
          });
        }
      } else if (response.status === 401) {
        // 401エラーは静かに処理（トークンは削除しない）
        // 管理者ログインやトークン更新時に誤って削除するのを防ぐ
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            // 環境不一致エラーの完全に静かな対応
            if (errorData.error === 'ENVIRONMENT_MISMATCH') {
              // 何もしない - ユーザーには見えない形で処理
            }
          } catch (e) {
            // JSONパースエラーも静かに処理
          }
        }
        // 401エラーは認証エラーなので、静かに処理
        setCounts({
          chat: 0,
          notifications: 0,
          stakes: 0,
          project: 0,
          store: 0,
          friends: 0,
        });
      } else {
        // エラー時もHTMLが返される可能性があるため、テキストで取得
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error('通知カウント取得エラー:', response.status, errorData);
        } else {
          const errorText = await response.text();
          console.error(
            '通知カウント取得エラー:',
            response.status,
            errorText.substring(0, 200)
          );
        }
        setCounts({
          chat: 0,
          notifications: 0,
          stakes: 0,
          project: 0,
          store: 0,
          friends: 0,
        });
      }
    } catch (err) {
      // 通知カウントは補助機能なので、全てのエラーを静かに処理
      const isNetworkError =
        err.message.includes('Network Error') ||
        err.message.includes('Failed to fetch');
      const isAuthError =
        err.message.includes('401') || err.message.includes('Unauthorized');

      // ネットワーク・認証エラー以外の重要なエラーのみログ出力
      if (!isNetworkError && !isAuthError) {
        console.debug('通知カウント取得スキップ:', err.message);
      }
      // ネットワーク・認証エラーは完全に静か

      setError(err.message);
      // エラー時も通知カウント0で継続（アプリ機能を阻害しない）
      setCounts({
        chat: 0,
        notifications: 0,
        stakes: 0,
        project: 0,
        store: 0,
        friends: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // 特定の通知カウントをリセット
  const resetCount = useCallback((type) => {
    setCounts((prev) => ({
      ...prev,
      [type]: 0,
    }));
  }, []);

  // 特定の通知カウントを増加
  const incrementCount = useCallback((type, amount = 1) => {
    setCounts((prev) => ({
      ...prev,
      [type]: prev[type] + amount,
    }));
  }, []);

  // 初回ロード
  useEffect(() => {
    fetchNotificationCounts();
  }, [fetchNotificationCounts]);

  // イベントベースの更新（フォーカス時・可視性変更時のみ）
  useEffect(() => {
    const handleFocus = () => {
      if (!loading) {
        fetchNotificationCounts();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && !loading) {
        fetchNotificationCounts();
      }
    };

    // フォーカス時とタブがアクティブになった時に更新
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loading, fetchNotificationCounts]);

  return {
    counts,
    loading,
    error,
    refetch: fetchNotificationCounts,
    resetCount,
    incrementCount,
  };
};
